import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { AuthGate } from '@/components/shell/AuthGate';
import { AppShell } from '@/components/shell/AppShell';
import { CallbackPage } from '@/components/shell/CallbackPage';
import { LoggedOutPage } from '@/components/shell/LoggedOutPage';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { PageSkeleton } from '@/components/shell/PageSkeleton';
import { FlagRoute } from '@/components/shell/FlagRoute';

// Route-level code splitting. Without these lazy()s the initial bundle
// pulls uPlot + react-table + @xyflow/react into the critical path even
// when the operator lands on `/` and never opens History or Alerts.
// Manual chunks in vite.config keep the heavy libs out of the shared
// vendor chunk so each page-chunk owns its own.
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const HistoryPage   = lazy(() => import('@/pages/HistoryPage').then((m) => ({ default: m.HistoryPage })));
const LogsPage      = lazy(() => import('@/pages/LogsPage').then((m) => ({ default: m.LogsPage })));
const AlertsPage    = lazy(() => import('@/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const PdmPage       = lazy(() => import('@/pages/PdmPage').then((m) => ({ default: m.PdmPage })));

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // 30-min in-memory retention so back-nav within the SPA reuses
      // warm queries instead of refetching. High-cost historian queries
      // pin staleTime: Infinity + gcTime: 10m at the call site (see
      // useHistorian / useHistorianPreview / useLogHistory).
      gcTime:    30 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/callback"  element={<CallbackPage />} />
          <Route path="/loggedout" element={<LoggedOutPage />} />
          <Route
            path="/*"
            element={
              <AuthGate>
                <Routes>
                  <Route element={<AppShell />}>
                    <Route index            element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
                    <Route path="history"   element={<Suspense fallback={<PageSkeleton />}><HistoryPage   /></Suspense>} />
                    <Route path="logs"      element={<Suspense fallback={<PageSkeleton />}><LogsPage      /></Suspense>} />
                    {/* Flagged pages 404 when navigated without the flag —
                        hiding the nav entry alone is cosmetic. The api
                        enforces the same flags server-side. */}
                    <Route path="alerts"    element={<FlagRoute flag="alerts-page"><Suspense fallback={<PageSkeleton />}><AlertsPage  /></Suspense></FlagRoute>} />
                    {/* /sensors removed: display-name management moved to the
                        admin portal (Companies → Sensors); users only consume
                        the resolved labels. */}
                    <Route path="pdm"       element={<FlagRoute flag="pdm-page"><Suspense fallback={<PageSkeleton />}><PdmPage      /></Suspense></FlagRoute>} />
                    <Route path="*"         element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </AuthGate>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}
